import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";

export interface Review {
  id: string;
  productId: number;
  customerId?: string;
  reviewerName: string;
  reviewerPhone: string;
  rating: number;
  reviewText: string;
  isVerified: boolean;
  createdAt: string;
  adminReply?: string;
  adminReplyAt?: string;
}

export interface ReviewStats {
  average: number;
  total: number;
  distribution: Record<1 | 2 | 3 | 4 | 5, number>;
}

function rowToReview(row: Record<string, unknown>): Review {
  return {
    id: String(row.id),
    productId: Number(row.product_id),
    customerId: row.customer_id ? String(row.customer_id) : undefined,
    reviewerName: String(row.reviewer_name ?? ""),
    reviewerPhone: String(row.reviewer_phone ?? ""),
    rating: Number(row.rating ?? 5),
    reviewText: String(row.review_text ?? ""),
    isVerified: Boolean(row.is_verified),
    createdAt: String(row.created_at ?? ""),
    adminReply: row.admin_reply ? String(row.admin_reply) : undefined,
    adminReplyAt: row.admin_reply_at ? String(row.admin_reply_at) : undefined,
  };
}

export function useReviews(productId: number) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchReviews = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("product_id", productId)
        .order("created_at", { ascending: false });
      if (!error && data) {
        setReviews(data.map(r => rowToReview(r as Record<string, unknown>)));
      }
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const addReview = useCallback(async (params: {
    reviewerName: string;
    reviewerPhone: string;
    rating: number;
    reviewText: string;
    customerId?: string;
  }): Promise<boolean> => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      // Check if this phone already reviewed this product
      const { data: existing } = await supabase
        .from("reviews")
        .select("id")
        .eq("product_id", productId)
        .eq("reviewer_phone", params.reviewerPhone)
        .maybeSingle();

      if (existing) {
        setSubmitError("لقد قمت بتقييم هذا المنتج مسبقاً");
        return false;
      }

      // Check if reviewer has purchased this product (verified buyer)
      const { data: orderData } = await supabase
        .from("orders")
        .select("id, items")
        .eq("phone", params.reviewerPhone)
        .maybeSingle();

      let isVerified = false;
      if (orderData?.items) {
        const items = orderData.items as { id: number }[];
        isVerified = items.some(item => item.id === productId);
      }

      const { data, error } = await supabase
        .from("reviews")
        .insert({
          product_id: productId,
          customer_id: params.customerId ?? null,
          reviewer_name: params.reviewerName,
          reviewer_phone: params.reviewerPhone,
          rating: params.rating,
          review_text: params.reviewText,
          is_verified: isVerified,
        })
        .select()
        .maybeSingle();

      if (error) {
        setSubmitError("حدث خطأ أثناء إرسال التقييم، حاول مرة أخرى");
        return false;
      }

      if (data) {
        setReviews(prev => [rowToReview(data as Record<string, unknown>), ...prev]);
      }
      setSubmitSuccess(true);
      return true;
    } catch {
      setSubmitError("حدث خطأ غير متوقع");
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [productId]);

  const stats: ReviewStats = {
    average: reviews.length
      ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10
      : 0,
    total: reviews.length,
    distribution: {
      5: reviews.filter(r => r.rating === 5).length,
      4: reviews.filter(r => r.rating === 4).length,
      3: reviews.filter(r => r.rating === 3).length,
      2: reviews.filter(r => r.rating === 2).length,
      1: reviews.filter(r => r.rating === 1).length,
    },
  };

  return {
    reviews, loading, stats,
    addReview, submitting, submitError, submitSuccess,
    refetch: fetchReviews,
    resetSubmit: () => { setSubmitError(null); setSubmitSuccess(false); },
  };
}
